import { ExternalLink, Pencil, RefreshCw, Trash2 } from "lucide-react";
import { useTranslation } from "react-i18next";
import ConnectionIcon from "./ConnectionIcon";
import MCPStatusBadge, { type MCPBadgeStatus } from "./MCPStatusBadge";
import type { MCPConnection } from "../types";
import type { LiveStatus } from "./StatusDot";

type Props = {
  connection: MCPConnection;
  status: LiveStatus;
  errorMessage?: string | null;
  authRequired: boolean;
  isActive: boolean;
  isTogglingActive?: boolean;
  onToggleActive: () => void;
  onRefresh: () => void;
  isRefreshing?: boolean;
  canManage: boolean;
  onEdit: () => void;
  onDelete: () => void;
  isOAuth: boolean;
  onReconnect: () => void;
  isReconnecting?: boolean;
};

const DrawerHeader = ({
  connection,
  status,
  errorMessage,
  authRequired,
  isActive,
  isTogglingActive = false,
  onToggleActive,
  onRefresh,
  isRefreshing = false,
  canManage,
  onEdit,
  onDelete,
  isOAuth,
  onReconnect,
  isReconnecting = false,
}: Props) => {
  const { t } = useTranslation();
  const badge: MCPBadgeStatus = !isActive
    ? "disabled"
    : connection.needsAttention || authRequired
      ? "needsAttention"
      : status;

  return (
    <header className="border-b border-border">
      <div className="flex min-h-[60px] items-center justify-between gap-3 px-4 py-2">
        <div className="flex min-w-0 items-center gap-4">
          <ConnectionIcon connection={connection} size="md" />
          <span className="truncate text-base text-foreground">{connection.name}</span>
        </div>
        <MCPStatusBadge status={badge} title={errorMessage ?? undefined} />
      </div>

      <div className="flex min-h-[60px] flex-wrap items-center justify-between gap-2 px-4 py-2">
        <div className="flex items-center gap-2">
          <button
            type="button"
            role="switch"
            aria-checked={isActive}
            aria-label={isActive ? t("mcpPage.actions.disable") : t("mcpPage.actions.enable")}
            disabled={isTogglingActive}
            onClick={onToggleActive}
            className={`flex h-[18px] w-[33px] shrink-0 cursor-pointer items-center rounded-full p-0.5 transition-colors disabled:cursor-not-allowed disabled:opacity-60 ${
              isActive ? "justify-end bg-primary" : "justify-start bg-muted"
            }`}
          >
            <span className="size-3.5 rounded-full bg-white shadow-sm" />
          </button>
          <span className="text-sm text-muted-foreground">
            {isActive ? t("mcpPage.status.enabled") : t("mcpPage.status.disabled")}
          </span>
        </div>

        <div className="flex flex-wrap items-center gap-2">
          {isOAuth && (
            <button
              type="button"
              onClick={onReconnect}
              disabled={isReconnecting}
              aria-busy={isReconnecting}
              className="flex h-9 items-center gap-2 rounded-[8px] border border-border bg-card px-4 text-sm font-medium text-foreground hover:bg-muted disabled:cursor-not-allowed disabled:opacity-60"
            >
              {t("mcpPage.actions.reauthenticate")}
              <ExternalLink className="size-3.5" aria-hidden="true" />
            </button>
          )}
          {canManage && (
            <>
              <button type="button" onClick={onDelete} aria-label={t("mcpPage.actions.delete")}
                className="flex size-9 items-center justify-center rounded-[8px] bg-destructive text-white hover:brightness-110">
                <Trash2 className="size-4" aria-hidden="true" />
              </button>
              <button type="button" onClick={onEdit} aria-label={t("mcpPage.actions.editDetails")}
                className="flex size-9 items-center justify-center rounded-[8px] border border-border bg-card text-foreground hover:bg-muted">
                <Pencil className="size-4" aria-hidden="true" />
              </button>
            </>
          )}
          <button type="button" onClick={onRefresh} disabled={isRefreshing} aria-label={t("mcpPage.actions.refresh")}
            className="flex size-9 items-center justify-center rounded-[8px] border border-border bg-card text-foreground hover:bg-muted disabled:cursor-not-allowed disabled:opacity-60">
            <RefreshCw className={`size-4 ${isRefreshing ? "animate-spin" : ""}`} aria-hidden="true" />
          </button>
        </div>
      </div>
    </header>
  );
};

export default DrawerHeader;
